import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { BankAccountService } from '../../services/bankAccount.service';
import { AccountService } from '../../services/account.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { Account } from '../../interfaces/accounting.interface';
import { BANK_ACCOUNT_TYPES, BankAccountCreateDTO } from '../../interfaces/bank/bankAccount.interface';
import { humanizeEnum } from '../../utils/format';
import { leafAccounts } from '../../utils/postingAccounts';

const EMPTY: BankAccountCreateDTO = {
  bankName: '', accountNumber: '', branchName: '', glCashAccountId: '', institutionId: '', currencyCode: '', accountType: null, overdraftLimit: null,
};

/** Create/edit a bank account (T8, frontend part 1). GL cash account is chosen from the ASSET
 * accounts; a non-blocking helper note flags a choice whose postingClass isn't CASH. */
const BankAccountForm: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const institutionId = AuthService.getSelectedInstitution()!;

  const [form, setForm] = useState<BankAccountCreateDTO>({ ...EMPTY, institutionId });
  const [assetAccounts, setAssetAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const accounts = await AccountService.getAccountsByType('ASSET');
        setAssetAccounts(leafAccounts(Array.isArray(accounts) ? accounts : (accounts?.content ?? [])));
      } catch (e) {
        toast.showApiError(e, 'Failed to load GL accounts');
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEdit || !id) return;
    (async () => {
      try {
        setLoading(true);
        const account = await BankAccountService.getById(id);
        setForm({
          bankName: account.bankName,
          accountNumber: account.accountNumber,
          branchName: account.branchName ?? '',
          glCashAccountId: account.glCashAccountId,
          institutionId: account.institutionId,
          currencyCode: account.currencyCode ?? '',
          accountType: account.accountType ?? null,
          overdraftLimit: account.overdraftLimit ?? null,
        });
      } catch (e) {
        toast.showApiError(e, 'Failed to load bank account');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedAccount = assetAccounts.find(a => a.id === form.glCashAccountId);
  const currencyValid = !form.currencyCode || /^[A-Z]{3}$/.test(form.currencyCode);
  const limitCanApply = !form.accountType || ['CURRENT', 'OD', 'CASH_CREDIT'].includes(form.accountType);
  const limitValid = form.overdraftLimit == null || (form.overdraftLimit >= 0 && (limitCanApply || form.overdraftLimit === 0));
  const valid = form.bankName.trim().length > 0 && form.accountNumber.trim().length > 0 && !!form.glCashAccountId
    && currencyValid && limitValid;

  const handleSave = async () => {
    const payload: BankAccountCreateDTO = {
      ...form,
      bankName: form.bankName.trim(),
      accountNumber: form.accountNumber.trim(),
      branchName: form.branchName?.trim() || undefined,
      currencyCode: form.currencyCode?.trim() || undefined,
      accountType: form.accountType || null,
      overdraftLimit: form.overdraftLimit ?? null,
    };
    try {
      setSaving(true);
      if (isEdit && id) {
        await BankAccountService.update(id, payload);
        toast.showSuccess('Bank account updated');
        navigate(`/banking/accounts/${id}`);
      } else {
        const created = await BankAccountService.create(payload);
        toast.showSuccess('Bank account created');
        navigate(`/banking/accounts/${created.id}`);
      }
    } catch (e) {
      toast.showApiError(e, 'Failed to save bank account');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 480 }} data-help-page="bank.accountForm.page">
      <Typography variant="h5" sx={{ mb: 2 }}>{isEdit ? 'Edit Bank Account' : 'New Bank Account'}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="bank.accountForm.section.details">
        <TextField data-help="bank.accountForm.field.bankName" label="Bank Name" value={form.bankName} required fullWidth
          onChange={e => setForm({ ...form, bankName: e.target.value })} />
        <TextField data-help="bank.accountForm.field.accountNumber" label="Account Number" value={form.accountNumber} required fullWidth
          onChange={e => setForm({ ...form, accountNumber: e.target.value })} />
        <TextField data-help="bank.accountForm.field.branch" label="Branch" value={form.branchName ?? ''} fullWidth
          onChange={e => setForm({ ...form, branchName: e.target.value })} />
        <TextField data-help="bank.accountForm.field.currency" label="Currency (blank = BDT)" value={form.currencyCode ?? ''} fullWidth
          error={!currencyValid}
          helperText={!currencyValid ? 'Use a 3-letter code such as USD' : 'Foreign-currency accounts only. The balance is revalued at month-end.'}
          inputProps={{ maxLength: 3 }}
          onChange={e => setForm({ ...form, currencyCode: e.target.value.toUpperCase() })} />
        <TextField data-help="bank.accountForm.field.accountType" select label="Account Type" value={form.accountType ?? ''} fullWidth
          data-testid="bank-account-type"
          onChange={e => setForm({ ...form, accountType: (e.target.value || null) as BankAccountCreateDTO['accountType'] })}>
          <MenuItem value=""><em>Not set</em></MenuItem>
          {BANK_ACCOUNT_TYPES.map(t => <MenuItem key={t} value={t}>{humanizeEnum(t)}</MenuItem>)}
        </TextField>
        <TextField data-help="bank.accountForm.field.overdraftLimit" label="Overdraft / Cash-credit Limit" type="number" fullWidth
          value={form.overdraftLimit ?? ''}
          error={!limitValid}
          helperText={!limitValid ? 'A limit applies to CURRENT, OD or CASH_CREDIT accounts only'
            : 'Sanctioned limit, if the account may run overdrawn. Used by the OD headroom view.'}
          inputProps={{ min: 0, step: '0.01' }}
          onChange={e => setForm({ ...form, overdraftLimit: e.target.value === '' ? null : Number(e.target.value) })} />
        <AccountAutocomplete data-help="bank.accountForm.field.glAccount"
          accounts={assetAccounts}
          value={form.glCashAccountId}
          onChange={id => setForm({ ...form, glCashAccountId: id })}
          label="GL Cash Account"
          required
        />
        {selectedAccount && selectedAccount.postingClass !== 'CASH' && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            This account's posting class is not CASH — double-check it's the right GL account.
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button data-help="bank.accountForm.save" variant="contained" disabled={saving || !valid} onClick={handleSave}>Save</Button>
          <Button data-help="bank.accountForm.cancel" variant="outlined" onClick={() => navigate(-1)}>Cancel</Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BankAccountForm;
